// The Settings switches as a tiny external store over localStorage, so plain
// functions (haptic()) and components (usePrefs) read the same value, and a
// change in one tab reaches the others through the `storage` event. The value
// is cached and only replaced on change — useSyncExternalStore requires a
// stable snapshot.

import { DEFAULT_PREFS, PREFS_STORAGE_KEY, readPrefs, type Prefs } from './prefs'

let cache: Prefs | null = null
const listeners = new Set<() => void>()

function load(): Prefs {
  try { return readPrefs(localStorage.getItem(PREFS_STORAGE_KEY)) } catch { return { ...DEFAULT_PREFS } }
}

export function getPrefs(): Prefs {
  return (cache ??= load())
}

export function setPref<K extends keyof Prefs>(key: K, value: Prefs[K]): void {
  cache = { ...getPrefs(), [key]: value }
  try { localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(cache)) } catch { /* private mode: this tab still follows */ }
  listeners.forEach(l => l())
}

function onStorage(e: StorageEvent) {
  if (e.key !== null && e.key !== PREFS_STORAGE_KEY) return
  cache = load()
  listeners.forEach(l => l())
}

export function subscribePrefs(listener: () => void): () => void {
  if (listeners.size === 0 && typeof window !== 'undefined') window.addEventListener('storage', onStorage)
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && typeof window !== 'undefined') window.removeEventListener('storage', onStorage)
  }
}

/** Tests only: forget the cached value so the next read goes to storage. */
export function resetPrefsCache(): void {
  cache = null
}
